import { useState, type ReactElement } from 'react';

import {
  QUESTION_TYPE_LABELS,
  type Question,
  type QuestionOption,
  type QuestionType,
} from '@/features/surveys/api';

const CHOICE_TYPES: QuestionType[] = ['single_choice', 'multi_choice', 'dropdown'];

function emptyQuestion(): Question {
  return {
    id: `q${Date.now().toString(36)}`,
    type: 'short_text',
    label: '',
    required: false,
  };
}

/**
 * 設問9型・条件分岐（visible_if）のエディタ（指示書 §5.1・§12 M5）。
 * 条件分岐は「特定の1問の回答が○○のときに表示する」という単一条件のみをUIで組み立てる
 * （JSON側は複数条件に対応しているが、UIはシンプルさを優先）。
 */
export function QuestionEditor({
  questions,
  onChange,
  readOnly = false,
}: {
  questions: Question[];
  onChange: (questions: Question[]) => void;
  readOnly?: boolean;
}): ReactElement {
  function updateQuestion(index: number, patch: Partial<Question>): void {
    const next = [...questions];
    next[index] = { ...next[index], ...patch } as Question;
    onChange(next);
  }

  function removeQuestion(index: number): void {
    onChange(questions.filter((_, i) => i !== index));
  }

  function addQuestion(): void {
    onChange([...questions, emptyQuestion()]);
  }

  function moveQuestion(index: number, direction: -1 | 1): void {
    const target = index + direction;
    if (target < 0 || target >= questions.length) return;
    const next = [...questions];
    const moved = next[index]!;
    next[index] = next[target]!;
    next[target] = moved;
    onChange(next);
  }

  return (
    <div className="flex flex-col gap-3">
      {questions.map((question, index) => (
        <QuestionCard
          key={question.id}
          question={question}
          allQuestions={questions}
          index={index}
          readOnly={readOnly}
          onChange={(patch) => updateQuestion(index, patch)}
          onRemove={() => removeQuestion(index)}
          onMove={(direction) => moveQuestion(index, direction)}
        />
      ))}
      {!readOnly && (
        <button
          type="button"
          onClick={addQuestion}
          className="self-start btn-secondary"
        >
          + 設問を追加
        </button>
      )}
      {questions.length === 0 && <p className="text-wptext-muted">設問がありません。</p>}
    </div>
  );
}

function QuestionCard({
  question,
  allQuestions,
  index,
  readOnly,
  onChange,
  onRemove,
  onMove,
}: {
  question: Question;
  allQuestions: Question[];
  index: number;
  readOnly: boolean;
  onChange: (patch: Partial<Question>) => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
}): ReactElement {
  const isChoice = CHOICE_TYPES.includes(question.type);
  const isMatrix = question.type === 'matrix';
  const isDescription = question.type === 'description';
  const otherQuestions = allQuestions.filter((q) => q.id !== question.id);
  const condition = question.visible_if?.conditions[0];

  function setOptions(options: QuestionOption[]): void {
    onChange({ options });
  }

  function setRows(rows: QuestionOption[]): void {
    onChange({ rows });
  }

  function onTypeChange(type: QuestionType): void {
    if (type === 'description') {
      onChange({ type, required: false, options: null, rows: null });
      return;
    }
    if (type !== 'matrix' && question.type === 'matrix') {
      onChange({ type, rows: null });
      return;
    }
    onChange({ type });
  }

  function setCondition(next: { question_id: string; operator: string; value: string } | null): void {
    if (next === null) {
      onChange({ visible_if: null });
      return;
    }
    onChange({
      visible_if: {
        op: 'all',
        conditions: [
          {
            question_id: next.question_id,
            operator: next.operator as 'eq' | 'neq' | 'in' | 'not_in',
            value: next.value,
          },
        ],
      },
    });
  }

  return (
    <div className="rounded-sm border border-wpborder bg-white p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-wptext-muted">#{index + 1}</span>
        <input
          value={question.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="設問文"
          disabled={readOnly}
          className="min-w-[240px] flex-1 rounded-sm border border-wpborder px-3 py-2"
        />
        <select
          value={question.type}
          onChange={(e) => onTypeChange(e.target.value as QuestionType)}
          disabled={readOnly}
          className="rounded-sm border border-wpborder px-3 py-2"
        >
          {Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {!isDescription && (
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={question.required}
              onChange={(e) => onChange({ required: e.target.checked })}
              disabled={readOnly}
            />
            必須
          </label>
        )}
        {!readOnly && (
          <div className="flex gap-1">
            <button type="button" onClick={() => onMove(-1)} className="text-wpaccent hover:underline">
              ↑
            </button>
            <button type="button" onClick={() => onMove(1)} className="text-wpaccent hover:underline">
              ↓
            </button>
            <button type="button" onClick={onRemove} className="text-wpnotice-error hover:underline">
              削除
            </button>
          </div>
        )}
      </div>

      {isDescription && (
        <p className="text-wptext-muted">
          回答欄のない案内文です。設問文がそのまま回答フォームに表示されます。
        </p>
      )}

      {isMatrix && (
        <div className="flex flex-col gap-2">
          <OptionsEditor
            label="行（設問文）"
            options={question.rows ?? []}
            onChange={setRows}
            readOnly={readOnly}
          />
          <OptionsEditor
            label="列（選択肢）"
            options={question.options ?? []}
            onChange={setOptions}
            readOnly={readOnly}
          />
        </div>
      )}

      {isChoice && (
        <OptionsEditor options={question.options ?? []} onChange={setOptions} readOnly={readOnly} />
      )}

      {otherQuestions.length > 0 && (
        <div className="mt-2 border-t border-wpborder pt-2">
          <BranchEditor
            condition={condition}
            candidates={otherQuestions}
            readOnly={readOnly}
            onChange={setCondition}
          />
        </div>
      )}
    </div>
  );
}

function OptionsEditor({
  options,
  onChange,
  readOnly,
  label = '選択肢',
}: {
  options: QuestionOption[];
  onChange: (options: QuestionOption[]) => void;
  readOnly: boolean;
  label?: string;
}): ReactElement {
  function update(index: number, patch: Partial<QuestionOption>): void {
    const next = [...options];
    next[index] = { ...next[index], ...patch } as QuestionOption;
    onChange(next);
  }

  function add(): void {
    onChange([...options, { value: `opt${options.length + 1}`, label: '' }]);
  }

  function remove(index: number): void {
    onChange(options.filter((_, i) => i !== index));
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-wptext-muted">{label}</span>
      {options.map((option, index) => (
        <div key={index} className="flex items-center gap-2">
          <input
            value={option.value}
            onChange={(e) => update(index, { value: e.target.value })}
            placeholder="値"
            disabled={readOnly}
            className="w-32 rounded-sm border border-wpborder px-3 py-2"
          />
          <input
            value={option.label}
            onChange={(e) => update(index, { label: e.target.value })}
            placeholder="表示ラベル"
            disabled={readOnly}
            className="flex-1 rounded-sm border border-wpborder px-3 py-2"
          />
          {!readOnly && (
            <button type="button" onClick={() => remove(index)} className="text-wpnotice-error hover:underline">
              削除
            </button>
          )}
        </div>
      ))}
      {!readOnly && (
        <button type="button" onClick={add} className="self-start text-wpaccent hover:underline">
          + 選択肢を追加
        </button>
      )}
    </div>
  );
}

const OPERATOR_LABELS: Record<string, string> = {
  eq: 'が次の値と一致するとき',
  neq: 'が次の値と一致しないとき',
  in: 'が次のいずれかのとき',
  not_in: 'が次のいずれでもないとき',
};

function BranchEditor({
  condition,
  candidates,
  readOnly,
  onChange,
}: {
  condition: { question_id: string; operator: string; value: unknown } | undefined;
  candidates: Question[];
  readOnly: boolean;
  onChange: (next: { question_id: string; operator: string; value: string } | null) => void;
}): ReactElement {
  const [enabled, setEnabled] = useState(Boolean(condition));

  if (!enabled) {
    return (
      <label className="flex items-center gap-1 text-wptext-muted">
        <input
          type="checkbox"
          disabled={readOnly}
          onChange={(e) => {
            setEnabled(e.target.checked);
            if (!e.target.checked) onChange(null);
          }}
        />
        条件分岐（他の設問の回答によって表示・非表示を切り替える）
      </label>
    );
  }

  const questionId = condition?.question_id ?? candidates[0]?.id ?? '';
  const operator = condition?.operator ?? 'eq';
  const value = typeof condition?.value === 'string' ? condition.value : '';

  return (
    <div className="flex flex-wrap items-center gap-2 text-wptext-muted">
      <label className="flex items-center gap-1">
        <input
          type="checkbox"
          checked
          disabled={readOnly}
          onChange={(e) => {
            setEnabled(e.target.checked);
            if (!e.target.checked) onChange(null);
          }}
        />
        条件分岐:
      </label>
      <select
        value={questionId}
        disabled={readOnly}
        onChange={(e) => onChange({ question_id: e.target.value, operator, value })}
        className="rounded-sm border border-wpborder px-3 py-2"
      >
        {candidates.map((q) => (
          <option key={q.id} value={q.id}>
            {q.label || q.id}
          </option>
        ))}
      </select>
      <select
        value={operator}
        disabled={readOnly}
        onChange={(e) => onChange({ question_id: questionId, operator: e.target.value, value })}
        className="rounded-sm border border-wpborder px-3 py-2"
      >
        {Object.entries(OPERATOR_LABELS).map(([op, label]) => (
          <option key={op} value={op}>
            {label}
          </option>
        ))}
      </select>
      <input
        value={value}
        disabled={readOnly}
        onChange={(e) => onChange({ question_id: questionId, operator, value: e.target.value })}
        placeholder="値"
        className="w-32 rounded-sm border border-wpborder px-3 py-2"
      />
    </div>
  );
}
